/**
 * PMPipelineBucket - Individual Pipeline Bucket
 *
 * Collapsible section showing projects in a specific pipeline stage.
 * Each bucket has helper text explaining what action removes items from it.
 */

import PMProjectCard from "./PMProjectCard";

// Color mappings for bucket accents
const BUCKET_COLORS = {
  blue: {
    border: "border-blue-200",
    headerBg: "bg-blue-50",
    badge: "bg-blue-100 text-blue-700",
    dot: "bg-blue-500",
  },
  yellow: {
    border: "border-yellow-200",
    headerBg: "bg-yellow-50",
    badge: "bg-yellow-100 text-yellow-700",
    dot: "bg-yellow-500",
  },
  orange: {
    border: "border-orange-200",
    headerBg: "bg-orange-50",
    badge: "bg-orange-100 text-orange-700",
    dot: "bg-orange-500",
  },
  green: {
    border: "border-green-200",
    headerBg: "bg-green-50",
    badge: "bg-green-100 text-green-700",
    dot: "bg-green-500",
  },
  purple: {
    border: "border-purple-200",
    headerBg: "bg-purple-50",
    badge: "bg-purple-100 text-purple-700",
    dot: "bg-purple-500",
  },
};

export default function PMPipelineBucket({
  bucket,
  expanded,
  onToggle,
  onSelectProject,
  onGenerateWorkOrder,
  onViewWorkOrder,
  onAssignToMe,
  onMarkPDFReady,
}) {
  const colors = BUCKET_COLORS[bucket.color] || BUCKET_COLORS.blue;
  const isEmpty = bucket.count === 0;

  return (
    <div
      className={`bg-white rounded-lg border ${colors.border} overflow-hidden`}
    >
      {/* Bucket Header */}
      <button
        onClick={onToggle}
        className={`w-full px-4 py-3 flex items-center justify-between ${colors.headerBg} hover:brightness-95 transition-all`}
      >
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${colors.dot}`}></span>
          <span className="text-xl">{bucket.icon}</span>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{bucket.name}</span>
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors.badge}`}
              >
                {bucket.count}
              </span>
            </div>
            <p className="text-xs text-gray-500">{bucket.description}</p>
          </div>
        </div>

        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Bucket Content */}
      {expanded && (
        <div className="border-t border-gray-100">
          {/* Helper Text */}
          {bucket.helperText && (
            <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 italic border-b border-gray-100">
              {bucket.helperText}
            </div>
          )}

          {/* Cards */}
          {isEmpty ? (
            <div className="px-4 py-8 text-center text-gray-400">
              <p className="text-sm">No items in this bucket</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {bucket.cards?.map((card, index) => (
                <PMProjectCard
                  key={card.id || card.quote_number || index}
                  card={card}
                  bucketId={bucket.id}
                  onSelect={() => onSelectProject?.(card)}
                  onGenerateWorkOrder={() => onGenerateWorkOrder?.(card)}
                  onViewWorkOrder={() => onViewWorkOrder?.(card)}
                  onAssignToMe={() => onAssignToMe?.(card)}
                  onMarkPDFReady={() => onMarkPDFReady?.(card)}
                />
              ))}

              {/* Show "Load More" if there are more than displayed */}
              {bucket.cards?.length < bucket.count && (
                <div className="px-4 py-3 text-center">
                  <button className="text-sm text-blue-600 hover:text-blue-700">
                    View all {bucket.count} items
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
